import { useState, useRef, useEffect, useCallback } from "react";
import { Card, Button, Row, Col, Tabs, Tab, Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import QuoteForm from "../common/QuoteForm/QuoteForm";
import "../Show/styles/style.css";
import { Service, Subservice } from "../../Services/types/Service";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import {
  useCreateThirdPartyServiceMutation,
  useDeleteThirdPartyServiceMutation,
  useListLanguageFieldsForAccountQuery,
  useUpdateThirdPartyServiceMutation,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import DataImportModal from "@components/DataImportModal/DataImportModal";
import useOauth from "@utils/customHooks/useOauth";
import API from "../../../utils/API/API";
import {
  calculatePrice,
  calculateCost,
  getLanguages,
  calculateMarkup,
} from "../quoteHelpers/quoteHelpers";
import { ServicesTable } from "../../Services/ServicesTable/ServicesTable";
import { Column } from "@components/EditableDataTable/types/column";
import UnsavedChangesModal from "@components/UnsavedChangesModal";
import { useGetQuote } from "./api";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { Quote } from "../types/Quote";
import useFlags from "@common/hooks/useFlags";

function Edit({
  project_id,
  account,
  permissions,
  project,
  revenueCalculationLabel,
  labels,
}) {
  const [authorizationCode] = useOauth();
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const navigate = useNavigate();
  const url = window.location.href;
  const id = url.split("/").slice(-1)?.[0] || "0";
  const { quoteData, quoteLoading, quoteFetching, refetch } = useGetQuote(id);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [previousQuote, setPreviousQuote] = useState<Quote | null>(null);
  const submitRef = useRef<any>();
  const [currentTab, setCurrentTab] = useState<string>("1");
  const [selectedLanguageField, setSelectedLanguageField] = useState(
    "Service Description"
  );
  const [updateThirdPartyServiceById] = useUpdateThirdPartyServiceMutation();
  const [createThirdPartyService] = useCreateThirdPartyServiceMutation();
  const [deleteThirdPartyService] = useDeleteThirdPartyServiceMutation();
  const [vendorRatesPresent, setVendorRatesPresent] = useState<boolean>(false);
  const [thirdPartyServices, setThirdPartyServices] =
    useState<Service[] | null>(null);
  // "Previous" third-party services state tracks the most recently saved state for discarding unsaved changes
  const [previousThirdPartyServices, setPreviousThirdPartyServices] =
    useState<Service[] | null>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    "Something went wrong. Your changes could not be saved at this time."
  );
  const [servicesUpdating, setServicesUpdating] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] =
    useState<Service | Subservice | null>(null);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [showDataImportModal, setShowDataImportModal] = useState(false);
  const [changes, setChanges] = useState<boolean>(false);
  const [showUnsavedChangesConfirmation, setShowUnsavedChangesConfirmation] =
    useState<boolean>(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const phases = project?.included?.filter(
    (item) => item.type === "project-phases"
  );
  const { data: languageFields, isLoading: languageFieldsLoading } =
    useListLanguageFieldsForAccountQuery({
      slug: accountSlug,
    });

  const { vendorQuotesPricingEnable } = useFlags();

  const [editingValues, setEditingValues] = useState<
    Record<string, { price?: string; markup?: string }>
  >({});

  const setEdit = (id: string, field: "price" | "markup", value: string) =>
    setEditingValues((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));

  useEffect(() => {
    if (!quote?.id && quoteData) {
      setQuote(quoteData);
      setPreviousQuote(quoteData);
    }
  }, [quoteLoading]);

  useEffect(() => {
    if (quote?.id && quoteData && !quoteFetching) {
      setQuote(quoteData);
      setPreviousQuote(quoteData);
    }
  }, [quoteFetching]);

  useEffect(() => {
    // Get third party services
    if (authorizationCode && !quoteLoading) {
      API.Get(
        `${apiHost}/${accountSlug}/v1/third-party-services?filter[active]=true&filter[source-type]=Quote&filter[source-id]=${id}&include=project-phase,subtasks`,
        authorizationCode
      ).then((response) => {
        let data = response.data.data;
        let includedData = response.data.included;
        let allSubservices = includedData?.filter(
          (item) => item.type === "third-party-services"
        );
        if (data !== null) {
          const services = data.map((service) => {
            if (!service.id) {
              throw new Error("Missing service.id");
            }
            const phase = phases?.find(
              (phase) =>
                service?.relationships?.["project-phase"]?.data?.id == phase.id
            );

            let subservices: any[] = [];
            allSubservices?.forEach((subservice) => {
              service?.relationships?.subtasks?.data?.forEach((subtask) => {
                if (
                  subservice.id == subtask.id &&
                  subservice.attributes.active
                ) {
                  subservices.push({
                    id: subservice?.id || "",
                    type: subservice.type,
                    name: subservice?.attributes?.name || "",
                    quantity: subservice?.attributes?.["quantity"] || 1,
                    price: calculatePrice(
                      subservice?.attributes?.["est-rate"] || 0,
                      subservice?.attributes?.markup || quote?.markup || 0,
                      revenueCalculationLabel
                    ),
                    priceOverride:
                      subservice?.attributes?.["price-override"] || null,
                    vendorResource:
                      !!subservice?.attributes?.["vendor-resource"],
                    hours: subservice?.attributes?.["est-hours"] || 0,
                    rate: subservice?.attributes?.["est-rate"] || 0,
                    markup: subservice?.attributes?.markup || 0,
                    originalDescription:
                      subservice?.attributes?.["original-description"],
                    serviceDescription:
                      subservice?.attributes?.["service-description"] || "",
                    sourceType: subservice?.attributes?.["source-type"],
                    sourceId: subservice?.attributes?.["source-id"],
                    position: subservice?.attributes?.position,
                    paymentFrequency:
                      subservice?.attributes?.["payment-frequency"],
                    languages:
                      Object.keys(
                        subservice?.attributes?.["original-languages"]
                      ).length > 0
                        ? subservice?.attributes?.languages
                        : getLanguages(languageFields?.data),
                    originalLanguages:
                      Object.keys(
                        subservice?.attributes?.["original-languages"]
                      ).length > 0
                        ? subservice?.attributes?.["original-languages"]
                        : getLanguages(languageFields?.data),
                    isChanged: false,
                  });
                }
              });
            });

            return {
              id: String(service.id),
              type: service.type,
              name: service?.attributes.name,
              phase: {
                label: phase?.attributes?.["name"] || "Select phase",
                value: phase?.id || 0,
              },
              quantity: service?.attributes?.quantity || 1,
              originalDescription:
                service?.attributes?.["original-description"],
              serviceDescription: service?.attributes?.["service-description"],
              subservices: subservices,
              sourceType: service?.attributes?.["source-type"],
              sourceId: service?.attributes?.["source-id"],
              price:
                service?.attributes?.["price-override"] ||
                calculatePrice(
                  service?.attributes?.["est-rate"] || 0,
                  service?.attributes?.markup || quote?.markup || 0,
                  revenueCalculationLabel
                ),
              priceOverride: service?.attributes?.["price-override"] || null,
              vendorResource: !!service?.attributes?.["vendor-resource"],
              hours: service?.attributes?.["est-hours"] || 0,
              rate: service?.attributes?.["est-rate"] || 0,
              markup: service?.attributes?.markup || 0,
              position: service?.attributes?.position || 1,
              paymentFrequency: service?.attributes?.["payment-frequency"],
              languages:
                Object.keys(service?.attributes?.["original-languages"])
                  .length > 0
                  ? service?.attributes?.languages
                  : getLanguages(languageFields?.data),
              originalLanguages:
                Object.keys(service?.attributes?.["original-languages"])
                  .length > 0
                  ? service?.attributes?.["original-languages"]
                  : getLanguages(languageFields?.data),
              isChanged: false,
            };
          });
          setThirdPartyServices(services);
          setVendorRatesPresent(
            services.reduce((retVal, curr) => {
              return (
                retVal ||
                curr.vendorResource ||
                curr.subservices.reduce((val, sub) => {
                  return val || sub.vendorResource;
                }, false)
              );
            }, false)
          );
          setPreviousThirdPartyServices(services);
          setServicesUpdating(false);
          refetch();
        }
      });
    }
  }, [authorizationCode, forceUpdate, quoteLoading]);

  useEffect(() => {
    if (!changes && quote?.id && previousQuote?.id) {
      if (JSON.stringify(previousQuote) !== JSON.stringify(quote)) {
        setChanges(true);
      }

      if (
        thirdPartyServices?.some(
          (service) =>
            service.isChanged || service?.subservices?.some((s) => s.isChanged)
        )
      ) {
        setChanges(true);
      }
    }
  }, [thirdPartyServices, quote]);

  const phaseOptions = phases.map((phase) => (
    <option key={phase.id} value={phase.id}>
      {phase?.attributes?.name}
    </option>
  ));

  const handleFailResponse = (msg) => {
    if (msg) {
      setErrorMessage(msg);
      setShowFailAlert(true);
    } else {
      setErrorMessage("Something went wrong.");
      setShowFailAlert(true);
    }
  };

  const addService = () => {
    const services = thirdPartyServices ? thirdPartyServices : [];
    const newThirdPartyServices = [
      ...services,
      {
        id: `new-${Math.floor(Math.random() * 100)}`,
        name: "",
        phase: {
          label: phaseOptions[0].props.children,
          value: phaseOptions[0].props.value,
        },
        quantity: 1,
        serviceDescription: "",
        subservices: [],
        sourceType: "Quote",
        sourceId: Number(id),
        price: 0,
        hours: 0,
        rate: 0,
        markup: 0,
        position: Number(thirdPartyServices?.length || 0) + 2,
        languages: getLanguages(languageFields?.data),
        originalLanguages: getLanguages(languageFields?.data),
      },
    ];
    setThirdPartyServices(newThirdPartyServices);
  };

  const triggerFail = (err) => {
    setServicesUpdating(false);
    handleFailResponse(err?.data?.errors?.[0]?.detail);
    setShowFailAlert(true);
  };

  const triggerSuccess = () => {
    setChanges(false);
    setForceUpdate(!forceUpdate);
    setPreviousThirdPartyServices(thirdPartyServices);
    setPreviousQuote(quote);
    if (currentTab !== "1") {
      setSuccessMessage(`${labels.quote} updated.`);
      setShowSuccessAlert(true);
    }
  };

  const getData = (item) => {
    let phaseId = item?.phase?.value;
    if (item.sourceType === "ThirdPartyService") {
      const service = thirdPartyServices?.find(
        (service) => service.id == item.sourceId
      );
      phaseId = service?.phase?.value || phaseOptions[0].props.value;
    }
    let data: any = {
      type: "third-party-services",
      id: Number(item.id),
      attributes: {
        name: item.name || "",
        quantity: item.quantity,
        "original-description": item.originalDescription,
        "service-description": item.serviceDescription,
        "est-hours": item.hours,
        "est-rate": item.rate,
        "price-override": item.priceOverride,
        "payment-frequency": item.paymentFrequency,
        "source-type": item.sourceType,
        "source-id": item.sourceId,
        position: item.position,
        "original-languages": item.originalLanguages,
        languages: item.languages,
        markup: item.markup,
      },
      relationships: {
        "project-phase": {
          data: { type: "project-phases", id: phaseId },
        },
      },
    };
    return data;
  };

  const saveServices = async () => {
    const thirdPartySubservices = new Set();
    thirdPartyServices?.forEach((service) => {
      service?.subservices?.forEach((subservice) => {
        thirdPartySubservices.add(subservice);
      });
    });
    const thirdPartySubservicesArray: any[] =
      Array.from(thirdPartySubservices) || [];
    const itemsToUpdate: any[] = (
      thirdPartyServices?.concat(thirdPartySubservicesArray) || []
    ).filter((item) => item?.isChanged && item?.sourceId);
    if (itemsToUpdate.length > 0) {
      for (const item of itemsToUpdate) {
        let data = getData(item);
        if (!item.id.includes("new")) {
          await updateThirdPartyServiceById({
            slug: accountSlug,
            id: Number(item.id),
            body: { data: data },
          }).catch((err) => {
            triggerFail(err);
          });
        } else {
          delete data?.id;
          await createThirdPartyService({
            slug: accountSlug,
            body: { data: data },
          })
            .then(async (res) => {
              let resData = res as any;
              const service = resData?.data?.data;
              if (item.subservices && item.subservices.length > 0) {
                const newSubservices = item.subservices.filter((subservice) =>
                  subservice.id.includes("new")
                );
                if (newSubservices) {
                  for (let sub of newSubservices) {
                    sub = { ...sub, sourceId: service.id };
                    let newSubData = getData(sub);
                    delete newSubData.id;
                    await createThirdPartyService({
                      slug: accountSlug,
                      body: { data: newSubData },
                    }).catch((err) => {
                      triggerFail(err);
                    });
                  }
                }
              }
            })
            .catch((err) => {
              triggerFail(err);
            });
        }
      }
      triggerSuccess();
    } else {
      triggerSuccess();
    }
  };

  const updateService = (item) => {
    if (!thirdPartyServices) return;

    if (item?.subservices) {
      //Updating service
      const updated = thirdPartyServices.map((s) => {
        return s.id == item.id ? { ...item, isChanged: true } : s;
      });
      setThirdPartyServices(updated);
    } else {
      //Updating subservice
      const updated = thirdPartyServices.map((s) => {
        return item.sourceId == s.id
          ? {
              ...s,
              subservices: s.subservices?.map((sub) =>
                sub.id == item.id ? { ...item, isChanged: true } : sub
              ),
              isChanged: true,
            }
          : s;
      });
      setThirdPartyServices(updated);
    }
  };

  const addSubservice = (selectedService) => {
    document
      .querySelectorAll(`.multi-collapse${selectedService.id}`)
      //Open subservice table for service across all tabs
      .forEach((table) => {
        if (!table.classList.contains("show")) {
          table.classList.add("show");
        }
      });

    setThirdPartyServices(
      thirdPartyServices?.map((service) => {
        const newSubservice: Subservice = {
          id: `new-${Math.floor(Math.random() * 100)}`,
          type: service.type,
          name: "",
          quantity: 1,
          originalDescription: "",
          serviceDescription: "",
          sourceType: "ThirdPartyService",
          sourceId: selectedService.id.toString() || "",
          position: Number(service?.subservices?.length || 0) + 2,
          isChanged: false,
          languages: getLanguages(languageFields?.data),
          originalLanguages: getLanguages(languageFields?.data),
          hours: 0,
          rate: 0,
          markup: 0,
        };
        if (selectedService?.id === service?.id) {
          service.subservices =
            service.subservices && service.subservices.length > 0
              ? ([...service.subservices, newSubservice] as Subservice[])
              : ([newSubservice] as Subservice[]);
        }
        return service;
      }) || []
    );
  };

  const filterOutDeletedServices = (serviceToDelete) => {
    if (serviceToDelete?.sourceType === "Quote") {
      setThirdPartyServices(
        thirdPartyServices?.filter(
          (service) => service.id !== serviceToDelete?.id
        ) || []
      );
    } else {
      setThirdPartyServices(
        thirdPartyServices?.map((service) => {
          if (
            service.id == serviceToDelete?.sourceId ||
            (typeof serviceToDelete.id === "string" &&
              serviceToDelete.id.includes("new"))
          ) {
            service.subservices = service.subservices?.filter(
              (subservice) => subservice.id !== serviceToDelete?.id
            );
          }
          return service;
        }) || []
      );
    }
  };

  const deleteService = (selectedService) => {
    setServiceToDelete(selectedService);
    if (
      selectedService &&
      typeof selectedService.id === "string" &&
      selectedService.id.includes("new")
    ) {
      filterOutDeletedServices(selectedService);
    } else {
      filterOutDeletedServices(selectedService);
      setShowDeleteConfirmModal(true);
    }
  };

  const executeServiceDeletion = () => {
    setShowDeleteConfirmModal(false);
    setServicesUpdating(true);
    if (serviceToDelete) {
      deleteThirdPartyService({
        slug: accountSlug,
        id: Number(serviceToDelete?.id),
      })
        .then((res) => {
          setSuccessMessage("Third Party Service deleted.");
          setShowSuccessAlert(true);
          setForceUpdate(!forceUpdate);
          setServicesUpdating(false);
        })
        .catch((err) => {
          handleFailResponse(err?.data?.errors?.[0]?.detail);
          setShowFailAlert(true);
          setForceUpdate(!forceUpdate);
          setServicesUpdating(false);
        });
    }
  };

  const getLanguageFieldValue = (item, isOurDescription) => {
    let value = "";
    if (selectedLanguageField === "Service Description") {
      if (
        isOurDescription &&
        item.serviceDescription &&
        item.serviceDescription !== ""
      ) {
        value = item.serviceDescription;
      } else {
        value = item.originalDescription;
      }
    } else {
      languageFields?.data?.forEach((lf) => {
        if (lf?.attributes?.name == selectedLanguageField) {
          if (isOurDescription) {
            value = item.languages?.[lf.attributes["sow-language"]];
          } else {
            value = item.originalLanguages?.[lf.attributes["sow-language"]];
          }
        }
      });
    }
    return value;
  };

  const allLanguageFields = [...(languageFields?.data || [])];
  allLanguageFields?.unshift({
    attributes: {
      name: "Service Description",
      "sow-language": "service-description",
    },
  });

  const getServiceColumns = (isOurDescription: boolean) => {
    let serviceColumns: Column[] = [
      {
        id: "phase",
        header() {
          return "Phase";
        },
        cell(service: Service) {
          return service.id === "noRow" ? (
            <div>{service.phase?.label}</div>
          ) : (
            <Form.Control
              as="select"
              value={service?.phase?.value}
              disabled={service.vendorResource}
              onChange={(e) =>
                updateService({
                  ...service,
                  phase: {
                    label: e?.target?.["selectedOptions"][0].innerHTML,
                    value: e.target?.value,
                  },
                })
              }
            >
              {phaseOptions}
            </Form.Control>
          );
        },
      },
      {
        header() {
          return "Service Name";
        },
        cell(service) {
          return service.id === "noRow" ? (
            <>{service.name}</>
          ) : (
            <Form.Control
              type="text"
              defaultValue={service.name}
              disabled={service.vendorResource}
              onChange={(e) =>
                updateService({ ...service, name: e.target.value })
              }
            />
          );
        },
      },
    ];
    if (currentTab !== "4") {
      serviceColumns.push({
        header() {
          return selectedLanguageField;
        },
        cell(service: Service) {
          return service.id === "noRow"
            ? ""
            : allLanguageFields?.map((lf) => (
                <Form.Control
                  key={lf.id}
                  style={
                    lf?.attributes?.name !== selectedLanguageField
                      ? { display: "none" }
                      : { display: "block" }
                  }
                  as="textarea"
                  type="text"
                  rows={1}
                  disabled={service.vendorResource}
                  defaultValue={
                    lf?.attributes?.name == selectedLanguageField
                      ? getLanguageFieldValue(service, isOurDescription)
                      : ""
                  }
                  onChange={(e) => {
                    if (selectedLanguageField === "Service Description") {
                      const key = isOurDescription
                        ? "serviceDescription"
                        : "originalDescription";
                      updateService({ ...service, [key]: e.target.value });
                    } else {
                      allLanguageFields?.forEach((lf) => {
                        if (lf?.attributes?.name == selectedLanguageField) {
                          const type = isOurDescription
                            ? "languages"
                            : "originalLanguages";
                          const key = lf.attributes["sow-language"];
                          updateService({
                            ...service,
                            [type]: {
                              ...service[type],
                              [key]: e.target.value,
                            },
                          });
                        }
                      });
                    }
                  }}
                />
              ));
        },
      });
    } else {
      let pricingColumns: Column[] = [
        {
          header() {
            return "Hours";
          },
          cell(service) {
            return service.id === "noRow" ? (
              ""
            ) : (
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                defaultValue={service.hours}
                disabled={service.vendorResource}
                onChange={(e) => {
                  const hours = Number(e.target.value);
                  updateService({
                    ...service,
                    hours,
                  });
                }}
              />
            );
          },
        },
        {
          header() {
            return "Hourly Cost";
          },
          cell(service) {
            if (service.id === "noRow") return "";
            const id = String(service.id);

            return (
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                defaultValue={service.rate ?? ""}
                disabled={service.vendorResource}
                onChange={(e) => {
                  const v = e.target.value;
                  const parsedRate = parseFloat(v);

                  if (!Number.isNaN(parsedRate)) {
                    const newPrice = calculatePrice(
                      parsedRate,
                      service.markup,
                      revenueCalculationLabel
                    );
                    setEdit(id, "price", newPrice.toFixed(2)); // updates displayed Hourly Price immediately
                  }
                }}
                onBlur={(e) => {
                  const parsedRate = parseFloat(e.target.value);
                  const finalRate = Number.isNaN(parsedRate)
                    ? service.rate
                    : parsedRate;

                  const finalPrice = calculatePrice(
                    finalRate,
                    service.markup,
                    revenueCalculationLabel
                  );

                  // commit numeric values internally
                  updateService({
                    ...service,
                    rate: finalRate,
                    price: finalPrice,
                    priceOverride: null,
                  });

                  // update display with formatted string
                  // setEdit(id, "rate", finalRate.toFixed(2));
                  setEdit(id, "price", finalPrice.toFixed(2));
                }}
              />
            );
          },
        },
        ...(vendorQuotesPricingEnable
          ? [
              {
                header() {
                  return revenueCalculationLabel === "margin"
                    ? "Target Margin"
                    : "Markup";
                },
                cell(service) {
                  if (service.id === "noRow") return "";
                  const id = String(service.id);
                  return service.id === "noRow" ? (
                    ""
                  ) : (
                    <Form.Control
                      type="number"
                      min="0"
                      step={0.001}
                      value={
                        editingValues[id]?.markup ??
                        Number(service.markup || 0).toFixed(3)
                      }
                      disabled={service.vendorResource}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEdit(id, "markup", v); // keep user typing freely

                        const parsedMarkup = parseFloat(v);
                        if (!Number.isNaN(parsedMarkup)) {
                          const newPrice = calculatePrice(
                            service.rate,
                            parsedMarkup,
                            revenueCalculationLabel
                          );
                          setEdit(id, "price", newPrice.toFixed(2)); // update Hourly Price immediately
                        }
                      }}
                      onBlur={(e) => {
                        const parsedMarkup = parseFloat(e.target.value);
                        const finalMarkup = Number.isNaN(parsedMarkup)
                          ? service.markup
                          : parseFloat(parsedMarkup.toFixed(3));

                        const finalPrice = calculatePrice(
                          service.rate,
                          finalMarkup,
                          revenueCalculationLabel
                        );

                        updateService({
                          ...service,
                          markup: finalMarkup,
                          price: finalPrice,
                          priceOverride: null,
                        });

                        setEdit(id, "markup", finalMarkup.toFixed(3));
                        setEdit(id, "price", finalPrice.toFixed(2));
                      }}
                    />
                  );
                },
              },
            ]
          : []),
        {
          header() {
            return "Hourly Price";
          },
          cell(service) {
            if (service.id === "noRow") return "";
            const id = String(service.id);
            return service.id === "noRow" ? (
              ""
            ) : (
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={
                  editingValues[id]?.price ??
                  Number(service.priceOverride ?? (service.price || 0)).toFixed(
                    2
                  )
                }
                disabled={service.vendorResource}
                onChange={(e) => {
                  const v = e.target.value;
                  setEdit(id, "price", v); // keep user typing freely

                  const parsedPrice = parseFloat(v);
                  if (!Number.isNaN(parsedPrice)) {
                    const newMarkup = vendorQuotesPricingEnable
                      ? calculateMarkup(
                          service.rate,
                          parsedPrice,
                          revenueCalculationLabel
                        )
                      : service.markup;

                    setEdit(id, "markup", newMarkup.toFixed(3)); // update Target Margin immediately
                  }
                }}
                onBlur={(e) => {
                  const parsedPrice = parseFloat(e.target.value);
                  const finalPrice = Number.isNaN(parsedPrice)
                    ? service.priceOverride ?? service.price
                    : parseFloat(parsedPrice.toFixed(2));

                  const finalMarkup = vendorQuotesPricingEnable
                    ? parseFloat(
                        calculateMarkup(
                          service.rate,
                          finalPrice,
                          revenueCalculationLabel
                        ).toFixed(3)
                      )
                    : service.markup;

                  updateService({
                    ...service,
                    price: finalPrice,
                    priceOverride: finalPrice,
                    markup: finalMarkup,
                  });

                  setEdit(id, "price", finalPrice.toFixed(2));
                  setEdit(id, "markup", finalMarkup.toFixed(3));
                }}
              />
            );
          },
        },
        {
          header() {
            return "Frequency";
          },
          cell(service) {
            return service.id === "noRow" ? (
              ""
            ) : (
              <Form.Control
                as="select"
                defaultValue={service.paymentFrequency}
                disabled={service.vendorResource}
                onChange={(e) =>
                  updateService({
                    ...service,
                    paymentFrequency: e.target.value,
                  })
                }
              >
                <option value="one_time">One Time</option>
                {quote?.billingFrequency?.value === "one_time" ? null : (
                  <option value={quote?.billingFrequency?.value}>
                    {" "}
                    {quote?.billingFrequency?.label}
                  </option>
                )}
              </Form.Control>
            );
          },
        },
      ];
      serviceColumns = serviceColumns.concat(pricingColumns);
    }
    return serviceColumns;
  };

  const getDesriptionData = (isOurDescription: boolean) => {
    if (
      !thirdPartyServices ||
      !languageFields ||
      languageFieldsLoading ||
      servicesUpdating
    ) {
      return <SmallSpinner />;
    } else {
      return (
        <div>
          <Row style={{ alignItems: "center", marginBottom: "20px" }}>
            <Col style={{ marginLeft: "16px" }} sm={7}>
              {thirdPartyServices.length > 0 && currentTab !== "4" ? (
                <Row style={{ alignItems: "center" }}>
                  <Col className="instructionalText" sm={4}>
                    Show / edit content for:
                  </Col>
                  <Col>
                    <div style={{ width: "300px", marginLeft: "-40px" }}>
                      <Form.Control
                        as="select"
                        value={selectedLanguageField}
                        onChange={(e) =>
                          setSelectedLanguageField(e.target.value)
                        }
                      >
                        <option key={0}>Service Description</option>
                        {languageFields?.data?.map((lf) => (
                          <option key={lf.id}>{lf?.attributes?.name}</option>
                        ))}
                      </Form.Control>
                    </div>
                  </Col>
                </Row>
              ) : null}
            </Col>
            <Col>
              <div className="vendorDescButtons" style={{ float: "right" }}>
                <Button
                  style={{ marginRight: "16px" }}
                  className="btnSeawhite"
                  onClick={() => setShowDataImportModal(true)}
                >
                  Import Services
                </Button>
                <Button
                  style={{ marginRight: "16px" }}
                  className="squareGreenButton"
                  onClick={() => addService()}
                >
                  Add Services
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form>
                <ServicesTable
                  servicesPassed={thirdPartyServices}
                  onUpdateServices={(services) =>
                    setThirdPartyServices(
                      thirdPartyServices?.map((s) => {
                        services?.forEach((service) => {
                          if (
                            service.id == s.id &&
                            service.sourceType === "Quote"
                          ) {
                            s = service;
                          }
                          if (
                            service.sourceType === "ThirdPartyService" &&
                            s.id == service.sourceId
                          ) {
                            s.subservices = services;
                          }
                        });
                        return s;
                      }) || []
                    )
                  }
                  serviceColumns={getServiceColumns(isOurDescription)}
                  subserviceColumns={getServiceColumns(
                    isOurDescription
                  )?.filter((c) => c.id !== "phase")}
                  addSubservice={addSubservice}
                  deleteService={deleteService}
                  hasSettings={true}
                  hasDragAndDrop={true}
                  isCollapsible={true}
                />
              </Form>

              {currentTab == "4" && vendorRatesPresent && (
                <p style={{ color: "#CC672F" }}>
                  {`*Fields disabled for services assigned to ${labels.resource}s`}
                </p>
              )}
            </Col>
          </Row>
        </div>
      );
    }
  };

  const onSave = () => {
    if (submitRef && submitRef.current && currentTab === "1") {
      submitRef.current.saveQuote(false);
      triggerSuccess();
    } else {
      setServicesUpdating(true);
      saveServices();
    }
    if (setShowUnsavedChangesConfirmation)
      setShowUnsavedChangesConfirmation(false);
    if (pendingTab) {
      setCurrentTab(pendingTab);
      setPendingTab(null);
    }
  };

  const confirmRevertChanges = () => {
    if (pendingTab) {
      setCurrentTab(pendingTab);
      setShowUnsavedChangesConfirmation(false);
      setPendingTab(null);
      setQuote(previousQuote);
      setThirdPartyServices(previousThirdPartyServices);
      setChanges(false);
      setForceUpdate(true);
    } else {
      navigate(`/projects/${project_id}/quotes`);
    }
  };

  const handleTabSelect = useCallback(
    (key: string | null) => {
      if (!key) return;
      if (changes) {
        setPendingTab(key); // Set the pending tab if there are unsaved changes
        setShowUnsavedChangesConfirmation(true);
      } else {
        setCurrentTab(key); // Directly set the active tab if no changes
      }
    },
    [changes]
  );

  return quoteLoading || !quote ? (
    <ScopeStackSpinner />
  ) : (
    <div className="editQuotePage">
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <ConfirmationModal
        show={showDeleteConfirmModal}
        title="Delete Third Party Service"
        message={`Are you sure you want to delete ${serviceToDelete?.name}?`}
        onConfirm={() => executeServiceDeletion()}
        onCancel={() => setShowDeleteConfirmModal(false)}
      />
      <Card>
        <Card.Header className="cardHeaderOverride">
          <div className="headerContainer">
            <div className="actionBtns">
              <Button
                style={{ marginRight: "20px" }}
                className="btnSeawhite"
                onClick={(e) =>
                  navigate(`/projects/${project_id}/quotes`, {
                    state: null,
                  })
                }
              >
                Cancel
              </Button>
              <div>
                <span className="text24">Edit {quote?.vendor?.label}</span>
              </div>
            </div>
            <div className="actionBtns">
              {!["view", "create"].includes(permissions.quotes) &&
                project?.data?.attributes?.status === "building" && (
                  <Button
                    style={{ marginRight: "16px", float: "right" }}
                    className="squareGreenButton"
                    onClick={onSave}
                    disabled={!changes}
                  >
                    Save
                  </Button>
                )}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Tabs
            activeKey={currentTab}
            className="mb-3"
            onSelect={handleTabSelect}
          >
            <Tab
              eventKey={1}
              title={"Quote Details"}
              tabClassName="quoteDetailsTab"
              className="quoteDetailsContent"
            >
              <QuoteForm
                quote={quote}
                setQuote={setQuote}
                setPreviousQuote={setPreviousQuote}
                project_id={project_id}
                account={account}
                ref={submitRef}
                permissions={permissions}
                project={project}
                revenueCalculationLabel={
                  quote?.revenueCalculationLabel || revenueCalculationLabel
                }
                label={labels.quote}
              />
            </Tab>
            <Tab
              eventKey={2}
              title={"Vendor Description of Services"}
              tabClassName="vendorDescriptionTab"
              className="vendorDescriptionContent"
            >
              {getDesriptionData(false)}
            </Tab>
            <Tab
              eventKey={3}
              title={"Our Description of Services"}
              tabClassName="ourDescriptionTab"
              className="ourDescriptionContent"
            >
              {getDesriptionData(true)}
            </Tab>
            <Tab
              eventKey={4}
              title={"Service Pricing"}
              tabClassName="servicePricingTab"
              className="servicePricingContent"
            >
              {getDesriptionData(true)}
            </Tab>
          </Tabs>
          <UnsavedChangesModal
            show={showUnsavedChangesConfirmation}
            handleCancel={() => {
              setShowUnsavedChangesConfirmation(false);
              setPendingTab(null);
            }}
            handleDontSave={confirmRevertChanges}
            handleSave={onSave}
          />
          <DataImportModal
            account={account}
            projectId={project_id}
            modalTitle="Import Quoted Services"
            importType="quoted_services"
            contextType="Quote"
            contextId={id}
            importInstructions="* Upload Services from Quote (CSV or Excel)"
            setErrorMessage={setErrorMessage}
            setLoadingIndicator={() => null}
            setShowFailAlert={setShowFailAlert}
            setShowDataImportModal={setShowDataImportModal}
            showDataImportModal={showDataImportModal}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

export default Edit;
